import React from "react";
import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DashboardAntDatePicker } from "src/components/reusable/antdDatePicker/DashboardAntDatePicker";
import { arabicCalendarLocale } from "src/components/reusable/antdDatePicker/arabicCalendarLocale";
import { getNumberLocale } from "src/utils/numberFormat";
import "./MonthRangePicker.scss";

interface MonthRangePickerProps {
  startDate: string;
  endDate: string;
  onRangeChange: (range: { start: string; end: string }) => void;
  maxPastMonths?: number;
  allowUnlimitedHistory?: boolean;
  lockEndToNextMonth?: boolean;
  /** Overrides the default "Start Month" placeholder (the left / start picker). */
  startPlaceholder?: string;
  /** Overrides the default "End Month" placeholder (the right / end picker). */
  endPlaceholder?: string;
}

const MonthRangePicker: React.FC<MonthRangePickerProps> = ({
  startDate,
  endDate,
  onRangeChange,
  lockEndToNextMonth = false,
  startPlaceholder,
  endPlaceholder,
}) => {
  const { t, i18n } = useTranslation();
  const [startHovered, setStartHovered] = React.useState(false);
  const isAr = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("ar");
  const antdLocale = isAr ? arabicCalendarLocale : enUS;
  const numberDisplayLocale = React.useMemo(() => getNumberLocale(i18n.language), [i18n.language]);
  const currentMonth = dayjs().endOf("month");
  const [localStart, setLocalStart] = React.useState(
    startDate ? dayjs(startDate, "YYYY-MM-DD").startOf("month") : null
  );
  const [localEnd, setLocalEnd] = React.useState(
    endDate ? dayjs(endDate, "YYYY-MM-DD").startOf("month") : null
  );

  React.useEffect(() => {
    setLocalStart(startDate ? dayjs(startDate, "YYYY-MM-DD").startOf("month") : null);
    setLocalEnd(endDate ? dayjs(endDate, "YYYY-MM-DD").startOf("month") : null);
  }, [endDate, startDate]);

  const handleStartChange = (date: dayjs.Dayjs | null) => {
    if (!date) {
      setLocalStart(null);
      return;
    }

    const nextStart = date.startOf("month");
    const safeStartMonth = nextStart.isAfter(currentMonth, "month")
      ? currentMonth.startOf("month")
      : nextStart;

    let endMonth: dayjs.Dayjs;
    if (lockEndToNextMonth) {
      const candidate = safeStartMonth.add(1, "month");
      endMonth = candidate.isAfter(currentMonth, "month")
        ? currentMonth.startOf("month")
        : candidate.startOf("month");
    } else {
      // End must be strictly after start. If the new start invalidates the current end, bump end forward.
      let candidate = localEnd;
      if (!candidate || !candidate.isAfter(safeStartMonth, "month")) {
        const next = safeStartMonth.add(1, "month");
        candidate = next.isAfter(currentMonth, "month")
          ? currentMonth.startOf("month")
          : next.startOf("month");
      }
      if (!candidate.isAfter(safeStartMonth, "month")) {
        candidate = safeStartMonth.endOf("month");
      }
      endMonth = candidate.isAfter(currentMonth, "month") ? currentMonth : candidate;
    }

    setLocalStart(safeStartMonth);
    setLocalEnd(endMonth.startOf("month"));

    onRangeChange({
      start: safeStartMonth.startOf("month").format("YYYY-MM-DD"),
      end: endMonth.startOf("month").format("YYYY-MM-DD"),
    });
  };

  const handleEndChange = (date: dayjs.Dayjs | null) => {
    if (!date) {
      setLocalEnd(null);
      return;
    }

    const safeEndMonth = date.startOf("month").isAfter(currentMonth, "month")
      ? currentMonth.startOf("month")
      : date.startOf("month");

    let startMonth: dayjs.Dayjs;
    if (lockEndToNextMonth) {
      startMonth = safeEndMonth.subtract(1, "month").startOf("month");
    } else if (!localStart) {
      setLocalEnd(safeEndMonth);
      return;
    } else {
      startMonth = localStart;
    }

    setLocalStart(startMonth);
    setLocalEnd(safeEndMonth);

    onRangeChange({
      start: startMonth.startOf("month").format("YYYY-MM-DD"),
      end: safeEndMonth.startOf("month").format("YYYY-MM-DD"),
    });
  };

  const formatMonthDisplay = React.useCallback(
    (date: dayjs.Dayjs) => {
      if (isAr) {
        return new Intl.DateTimeFormat(numberDisplayLocale, {
          month: "short",
          year: "numeric",
        }).format(date.toDate());
      }
      return date.format("MMM, YYYY");
    },
    [isAr, numberDisplayLocale]
  );

  return (
    <ConfigProvider locale={antdLocale} direction={isAr ? "rtl" : "ltr"}>
      <div className="month-range-picker-container">
        <div
          className={lockEndToNextMonth ? "month-range-picker__start-wrapper" : undefined}
          onMouseEnter={() => lockEndToNextMonth && setStartHovered(true)}
          onMouseLeave={() => setStartHovered(false)}
        >
          <DashboardAntDatePicker
            value={localStart}
            picker="month"
            format={formatMonthDisplay}
            onChange={handleStartChange}
            placeholder={startPlaceholder ?? t("monthRangePicker.startMonth")}
            className="dashboard-date-picker"
            placement={isAr ? "bottomRight" : "bottomLeft"}
            disabled={lockEndToNextMonth}
            disabledDate={date => date.isAfter(currentMonth, "month")}
          />
          {lockEndToNextMonth && startHovered && (
            <div className="month-range-picker__lock-tooltip" role="tooltip">
              <div className="month-range-picker__lock-tooltip__bar" />
              <div className="month-range-picker__lock-tooltip__content">
                <span className="month-range-picker__lock-tooltip__title">
                  {t("monthRangePicker.lockTooltip.title")}
                </span>
                <p className="month-range-picker__lock-tooltip__body">
                  {t("monthRangePicker.lockTooltip.body")}
                </p>
                <span className="month-range-picker__lock-tooltip__example">
                  {t("monthRangePicker.lockTooltip.example")}
                </span>
              </div>
            </div>
          )}
        </div>
        <span className="month-range-picker__vs">{t("monthRangePicker.vs")}</span>
        <DashboardAntDatePicker
          value={localEnd}
          picker="month"
          format={formatMonthDisplay}
          onChange={handleEndChange}
          placeholder={endPlaceholder ?? t("monthRangePicker.endMonth")}
          className="dashboard-date-picker"
          placement={isAr ? "bottomRight" : "bottomLeft"}
          disabledDate={date => {
            if (date.isAfter(currentMonth, "month")) {
              return true;
            }
            if (lockEndToNextMonth || !localStart) {
              return false;
            }
            return (
              date.isBefore(localStart, "month") ||
              date.isSame(localStart, "month")
            );
          }}
        />
      </div>
    </ConfigProvider>
  );
};

export default MonthRangePicker;
